import { useState } from "react";
import { useSnackbar } from "../../core/contexts/SnackbarContext";
import { ApiFactory } from "../../core/config/ApiFactory";
import { useRegex } from "../../core/hooks/useRegex";
import { useMascara } from "../../core/hooks/useMascara";
import { ICliente } from "../interface/ICliente";
import { IEndereco } from "../../core/interfaces/IEndereco";

export interface IFiltrosExportacaoClientes {
    nome?: string;
    telefone?: string;
    observacao?: string;
}

export const useClientes = () => {
    const [loading, setLoading] = useState(false);
    const [loadingExportar, setLoadingExportar] = useState(false);
    const snackbar = useSnackbar();
    const { formatarTelefoneParaPadrao } = useMascara();
    const apiClientes = ApiFactory.getApi("MS_CLIENTES");

    const salvar = async (cliente: ICliente) => {
        setLoading(true);
        try {
            // Formatar telefone antes de salvar
            const clienteFormatado = {
                ...cliente,
                telefone: cliente.telefone ? formatarTelefoneParaPadrao(cliente.telefone) || cliente.telefone : cliente.telefone
            };
            const { data } = await apiClientes.post('/', clienteFormatado);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao cadastrar o cliente", { severity: "error" });
            return null;
        } finally {
            setLoading(false);
        }
    }

    const excluir = async (clienteUuid: string) => {
        setLoading(true);
        try {
            await apiClientes.delete(`/${clienteUuid}`);
            return true;
        } catch (error) {
            snackbar("Ocorreu um erro ao cadastrar o cliente", { severity: "error" });
            return false;
        } finally {
            setLoading(false);
        }
    }

    const buscarClientesPorNomeOuTelefone = async (filtro: string, filtroTipo: 'nome' | 'telefone') => {
        if(!filtro || !filtro.length) {
            return null;
        }

        setLoading(true);
        try {
            if (filtroTipo === 'telefone') {
                // Remove caracteres não numéricos para análise
                let numerosLimpos = filtro.replace(/\D/g, "");
                
                // Remove código do país (55) se presente
                if (numerosLimpos.length > 11 && numerosLimpos.startsWith("55")) {
                    numerosLimpos = numerosLimpos.substring(2);
                }
                
                // Remove DDD se presente (primeiros 2 dígitos)
                let numeroSemDDD = numerosLimpos;
                let ddd = '';
                if (numerosLimpos.length >= 10) {
                    ddd = numerosLimpos.substring(0, 2);
                    numeroSemDDD = numerosLimpos.substring(2);
                }
                
                // Tenta primeiro com o formato formatado completo
                const filtroFormatado = formatarTelefoneParaPadrao(filtro) || filtro;
                let { data } = await apiClientes.get(`?${filtroTipo}=${filtroFormatado}`);
                
                // Se encontrou, retorna
                if (data && data.length > 0) {
                    return data;
                }
                
                // Se o número sem DDD tem 8 dígitos e começa com 9, tenta também com 9 dígitos (adicionando 9 na frente)
                if (numeroSemDDD.length === 8 && numeroSemDDD.startsWith('9')) {
                    const numeroCom9 = '9' + numeroSemDDD; // Adiciona 9 na frente: 99073080 -> 999073080
                    
                    // Formata com DDD se tiver
                    if (ddd) {
                        const formatoCom9 = formatarTelefoneParaPadrao(ddd + numeroCom9);
                        if (formatoCom9) {
                            const resultadoCom9 = await apiClientes.get(`?${filtroTipo}=${formatoCom9}`);
                            if (resultadoCom9.data && resultadoCom9.data.length > 0) {
                                return resultadoCom9.data;
                            }
                        }
                    }
                    
                    // Tenta também sem DDD
                    const numeroCom9Formatado = numeroCom9.replace(/^(\d{5})(\d{4})$/, "$1-$2");
                    const resultadoSemDDDCom9 = await apiClientes.get(`?${filtroTipo}=${numeroCom9Formatado}`);
                    if (resultadoSemDDDCom9.data && resultadoSemDDDCom9.data.length > 0) {
                        return resultadoSemDDDCom9.data;
                    }
                }
                
                // Se o número sem DDD tem 9 dígitos e começa com 9, tenta também sem o primeiro 9 (8 dígitos)
                if (numeroSemDDD.length === 9 && numeroSemDDD.startsWith('9')) {
                    const numeroSem9 = numeroSemDDD.substring(1); // Remove o primeiro 9: 999073080 -> 99073080
                    
                    // Formata com DDD se tiver
                    if (ddd) {
                        const formatoSem9 = formatarTelefoneParaPadrao(ddd + numeroSem9);
                        if (formatoSem9) {
                            const resultadoSem9 = await apiClientes.get(`?${filtroTipo}=${formatoSem9}`);
                            if (resultadoSem9.data && resultadoSem9.data.length > 0) {
                                return resultadoSem9.data;
                            }
                        }
                    }
                    
                    // Tenta também sem DDD
                    const numeroSem9Formatado = numeroSem9.replace(/^(\d{4})(\d{4})$/, "$1-$2");
                    const resultadoSemDDDSem9 = await apiClientes.get(`?${filtroTipo}=${numeroSem9Formatado}`);
                    if (resultadoSemDDDSem9.data && resultadoSemDDDSem9.data.length > 0) {
                        return resultadoSemDDDSem9.data;
                    }
                }
                
                // Tenta também apenas o número sem DDD formatado
                if (numeroSemDDD.length >= 8) {
                    const numeroSemDDDFormatado = numeroSemDDD.length === 9 
                        ? numeroSemDDD.replace(/^(\d{5})(\d{4})$/, "$1-$2")
                        : numeroSemDDD.replace(/^(\d{4})(\d{4})$/, "$1-$2");
                    const resultadoSemDDD = await apiClientes.get(`?${filtroTipo}=${numeroSemDDDFormatado}`);
                    if (resultadoSemDDD.data && resultadoSemDDD.data.length > 0) {
                        return resultadoSemDDD.data;
                    }
                }
                
                // Tenta também apenas os números sem formatação
                const resultadoNumeros = await apiClientes.get(`?${filtroTipo}=${numerosLimpos}`);
                if (resultadoNumeros.data && resultadoNumeros.data.length > 0) {
                    return resultadoNumeros.data;
                }
                
                // Se nenhuma busca encontrou, retorna array vazio
                return [];
            } else {
                // Para busca por nome, usa o filtro direto
                const { data } = await apiClientes.get(`?${filtroTipo}=${filtro}`);
                return data ?? [];
            }
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar os clientes", { severity: "error" });
            return null; // Retorna null em caso de erro para diferenciar de "não encontrado"
        } finally {
            setLoading(false);
        }
    } 

    const buscarPorUuid = async (clienteUuid: string): Promise<ICliente | null> => {
        if (!clienteUuid) {
            return null;
        }

        setLoading(true);
        try {
            const { data } = await apiClientes.get(`/${clienteUuid}`);
            return data ?? null;
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar o cliente", { severity: "error" });
            return null;
        } finally {
            setLoading(false);
        }
    }

    const listarEnderecos = async (clienteUuid: string): Promise<IEndereco[]> => {
        if(!clienteUuid) {
            snackbar("Cliente inválido", { severity: "error" });
        }
        
        setLoading(true);
        try {
            const { data } = await apiClientes.get(`/${clienteUuid}/enderecos`);
            return data ?? [];
        } catch (error) {
            snackbar("Ocorreu um erro ao buscar os endereços do cliente", { severity: "error" });
            return [];
        } finally {
            setLoading(false);
        }
    }

    const salvarEndereco = async (endereco: IEndereco, clienteUuid: string) => {
        if(!endereco) {
            snackbar("Endereço inválido", { severity: "error" });
        }
        
        setLoading(true);
        try {
            const { data } = await apiClientes.post(`/${clienteUuid}/endereco`, endereco);
            return data.endereco;
        } catch (error) {
            snackbar("Ocorreu um erro ao salvar o endereço do cliente", { severity: "error" });
            return null;
        } finally {
            setLoading(false);
        }
    }

    const excluirEndereco = async (enderecoUuid: string, clienteUuid: string) => {
        if(!enderecoUuid) {
            snackbar("Endereço inválido", { severity: "error" });
        }

        if(!clienteUuid) {
            snackbar("Cliente inválido", { severity: "error" });
        }
        
        setLoading(true);
        try {
            const { data } = await apiClientes.delete(`/${clienteUuid}/endereco/${enderecoUuid}`);
            if(data) {
                snackbar("Endereço excluído com sucesso");
            }
            return true;
        } catch (error) {
            snackbar("Ocorreu um erro ao excluir o endereço do cliente", { severity: "error" });
            return null;
        } finally {
            setLoading(false);
        }
    }

    const consultarTotal = async () => {
        setLoading(true);
        try {
            const { data } = await apiClientes.get(`/total`);
            return data;
        } catch (error) {
            snackbar("Ocorreu um erro ao consultar o número total de clientes", { severity: "error" });
            return null;
        } finally {
            setLoading(false);
        }
    }

    const exportar = async (filtros?: IFiltrosExportacaoClientes) => {
        setLoadingExportar(true);
        try {
            const parametros = new URLSearchParams();

            if (filtros?.nome) {
                parametros.append("nome", filtros.nome);
            }

            if (filtros?.telefone) {
                parametros.append("telefone", filtros.telefone);
            }

            if (filtros?.observacao) {
                parametros.append("observacao", filtros.observacao);
            }

            const queryString = parametros.toString();
            const response = await apiClientes.get(
                queryString ? `/exportar?${queryString}` : "/exportar",
                { responseType: "blob" }
            );
            const dataHoje = new Date().toISOString().split("T")[0];
            const urlBlob = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = urlBlob;
            link.setAttribute("download", `clientes-${dataHoje}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlBlob);
            snackbar("Planilha de clientes gerada com sucesso", { severity: "success" });
            return true;
        } catch (error) {
            snackbar("Ocorreu um erro ao exportar os clientes", { severity: "error" });
            return false;
        } finally {
            setLoadingExportar(false);
        }
    }

    const excluirEmLote = async (clientesUuid: string[]) => {
        setLoading(true);
        try {
            const resultados = [];
            
            for (const uuid of clientesUuid) {
                try {
                    await apiClientes.delete(`/${uuid}`);
                    resultados.push({ uuid, sucesso: true });
                } catch (error) {
                    resultados.push({ uuid, sucesso: false, erro: error });
                }
            }
            
            const sucessos = resultados.filter(r => r.sucesso).length;
            const falhas = resultados.filter(r => !r.sucesso).length;
            
            if (falhas > 0) {
                snackbar(`${sucessos} cliente(s) excluído(s) com sucesso. ${falhas} falha(s).`, { 
                    severity: falhas === clientesUuid.length ? "error" : "warning" 
                });
            } else {
                snackbar(`${sucessos} cliente(s) excluído(s) com sucesso`, { severity: "success" });
            }
            
            return resultados;
        } catch (error) {
            snackbar("Ocorreu um erro ao excluir os clientes", { severity: "error" });
            return null;
        } finally {
            setLoading(false);
        }
    }

    return { buscarClientesPorNomeOuTelefone, buscarPorUuid, excluirEndereco, loading, loadingExportar, salvar, listarEnderecos, salvarEndereco, excluir, excluirEmLote, consultarTotal, exportar }
}